import { useRandomId, warnAboutMissingStyles } from '@entur/utils';
import React, { useState, useEffect, cloneElement } from 'react';
import classNames from 'classnames';
import { RightArrowIcon, MenuIcon, LeftArrowIcon, VerticalDotsIcon, UpArrowIcon, DownArrowIcon } from '@entur/icons';
import { ExpandArrow, BaseExpand } from '@entur/expand';
import { Menu, MenuButton, MenuList, MenuItem, MenuLink } from '@reach/menu-button';
import { VisuallyHidden } from '@entur/a11y';
import { useContrast, Contrast } from '@entur/layout';
import { Label } from '@entur/typography';
import { IconButton } from '@entur/button';
var BreadcrumbNavigation = function BreadcrumbNavigation(_ref) {
  var _ref$ariaLabel = _ref['aria-label'],
    ariaLabel = _ref$ariaLabel === void 0 ? 'Brødsmulesti' : _ref$ariaLabel,
    children = _ref.children;
  return React.createElement("nav", {
    "aria-label": ariaLabel
  }, React.createElement("ol", {
    className: "eds-breadcrumbs"
  }, React.Children.map(children, function (child, index) {
    // @ts-expect-error should check if children are correct but for now it is only mentioned in the documentation
    return React.cloneElement(child, {
      isCurrent: index + 1 === React.Children.count(children)
    });
  })));
};
function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
var _excluded$8 = ["className", "isCurrent", "as"];
var defaultElement$1 = 'a';
var BreadcrumbItem = function BreadcrumbItem(_ref) {
  var className = _ref.className,
    isCurrent = _ref.isCurrent,
    as = _ref.as,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$8);
  var Element = as || defaultElement$1;
  return React.createElement(React.Fragment, null, React.createElement("li", {
    className: classNames('eds-breadcrumb__item', className)
  }, React.createElement(Element, _extends({
    "aria-current": isCurrent ? 'page' : undefined,
    className: classNames('eds-breadcrumb__link', {
      'eds-breadcrumb__link--current': isCurrent
    })
  }, rest))), !isCurrent && React.createElement(RightArrowIcon, {
    className: "eds-breadcrumb__separator",
    inline: true,
    role: "presentation"
  }));
};
var _excluded$7 = ["className", "children", "size"];
var SideNavigation = function SideNavigation(_ref) {
  var className = _ref.className,
    children = _ref.children,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'medium' : _ref$size,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$7);
  if (!children || !React.Children.count(children)) {
    return null;
  }
  return React.createElement("ul", _extends({
    className: classNames('eds-side-navigation', {
      'eds-side-navigation--small': size === 'small'
    }, className)
  }, rest), children);
};
/** This is required to check that the Menu */
SideNavigation.__IS_ENTUR_MENU__ = true;
function useControllableProp(_ref) {
  var prop = _ref.prop,
    _ref$updater = _ref.updater,
    updater = _ref$updater === void 0 ? function () {
      return undefined;
    } : _ref$updater,
    defaultValue = _ref.defaultValue;
  var _useState = useState(defaultValue),
    internalState = _useState[0],
    setInternalState = _useState[1];
  useEffect(function () {
    if (prop !== undefined) {
      setInternalState(prop);
    }
  }, [prop]);
  return prop === undefined ? [internalState, setInternalState] : [prop, updater];
}
var _excluded$6 = ["className", "children", "size", "collapsed", "onCollapseToggle", "collapsibleButtonPosition", "openSideMenuAriaLabel", "closeSideMenuAriaLabel"];
var CollapsibleSideNavigation = function CollapsibleSideNavigation(_ref) {
  var className = _ref.className,
    children = _ref.children,
    size = _ref.size,
    collapsible = _ref.collapsed,
    onCollapseToggle = _ref.onCollapseToggle,
    _ref$collapsibleButto = _ref.collapsibleButtonPosition,
    collapsibleButtonPosition = _ref$collapsibleButto === void 0 ? '50%' : _ref$collapsibleButto,
    _ref$openSideMenuAria = _ref.openSideMenuAriaLabel,
    openSideMenuAriaLabel = _ref$openSideMenuAria === void 0 ? 'Åpne sidemeny' : _ref$openSideMenuAria,
    _ref$closeSideMenuAri = _ref.closeSideMenuAriaLabel,
    closeSideMenuAriaLabel = _ref$closeSideMenuAri === void 0 ? 'Lukk sidemeny' : _ref$closeSideMenuAri,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$6);
  var _useControllableProp = useControllableProp({
      prop: collapsible,
      defaultValue: false,
      updater: onCollapseToggle
    }),
    collapsedMenu = _useControllableProp[0],
    setCollapsedMenu = _useControllableProp[1];
  return React.createElement(SideNavigationContext.Provider, {
    value: {
      isCollapsed: collapsedMenu
    }
  }, React.createElement("ul", _extends({
    className: classNames('eds-side-navigation', {
      'eds-side-navigation--small': size === 'small'
    }, {
      'eds-side-navigation--collapsed': collapsedMenu
    }, className)
  }, rest), children, React.createElement("button", {
    className: "eds-side-navigation__collapse-button",
    onClick: function onClick() {
      return setCollapsedMenu(!collapsedMenu);
    },
    style: {
      top: "" + collapsibleButtonPosition
    }
  }, collapsedMenu ? React.createElement(MenuIcon, {
    "aria-label": openSideMenuAriaLabel
  }) : React.createElement(LeftArrowIcon, {
    "aria-label": closeSideMenuAriaLabel
  }))));
};
var SideNavigationContext = /*#__PURE__*/React.createContext({
  isCollapsed: false
});
var useSideNavigationContext = function useSideNavigationContext() {
  var context = React.useContext(SideNavigationContext);
  if (!context) {
    console.error('Error reading SideNavigationContext. Please contact maintainer of @entur/menu');
  }
  return context;
};
var useShowDelayedLabel = function useShowDelayedLabel(isCollapsed) {
  var _useState = useState(true),
    showLabel = _useState[0],
    setShowLabel = _useState[1];
  var hideDelay = 50;
  var showDelay = 200;
  useEffect(function () {
    if (isCollapsed) {
      setTimeout(function () {
        setShowLabel(false);
      }, hideDelay);
    }
    if (!isCollapsed) {
      setTimeout(function () {
        setShowLabel(true);
      }, showDelay);
    }
    return function () {
      return undefined;
    };
  }, [isCollapsed]);
  return [showLabel];
};
var _excluded$5 = ["className", "active", "subMenu", "icon", "children", "as"],
  _excluded2$1 = ["children"],
  _excluded3$1 = ["active", "disabled", "children", "forceExpandSubMenus", "as"];
function isActiveRecursively(child) {
  if (!child.props) {
    return false;
  }
  if (child.props.active) {
    return true;
  }
  if (!child.props.children) {
    return false;
  }
  return React.Children.toArray(child.props.children).some(function (child) {
    return isActiveRecursively(child);
  });
}
var defaultElementBaseItem = 'a';
var BaseSideNavigationItem = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var className = _ref.className,
    _ref$active = _ref.active,
    active = _ref$active === void 0 ? false : _ref$active,
    subMenu = _ref.subMenu,
    icon = _ref.icon,
    children = _ref.children,
    as = _ref.as,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$5);
  var Element = as || defaultElementBaseItem;
  var _useSideNavigationCon = useSideNavigationContext(),
    isCollapsed = _useSideNavigationCon.isCollapsed;
  var _useShowDelayedLabel = useShowDelayedLabel(isCollapsed),
    showLabel = _useShowDelayedLabel[0];
  return React.createElement("li", {
    className: classNames('eds-side-navigation__item', className)
  }, React.createElement(Element, _extends({
    className: classNames('eds-side-navigation__click-target', {
      'eds-side-navigation__click-target--active': active
    }),
    "aria-label": isCollapsed ? children : undefined,
    "aria-current": active ? 'page' : undefined,
    ref: ref
  }, rest), icon, showLabel && children), subMenu);
});
var DisabledSideNavigationItem = /*#__PURE__*/React.forwardRef(function (_ref2, ref) {
  var children = _ref2.children,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2$1);
  return React.createElement(BaseSideNavigationItem, _extends({
    as: "button",
    disabled: true,
    "aria-disabled": true,
    ref: ref,
    type: "button"
  }, rest), children);
});
var defaultElementItem = 'a';
var SideNavigationItem = /*#__PURE__*/React.forwardRef(function (_ref3, ref) {
  var active = _ref3.active,
    disabled = _ref3.disabled,
    children = _ref3.children,
    forceExpandSubMenus = _ref3.forceExpandSubMenus,
    as = _ref3.as,
    rest = _objectWithoutPropertiesLoose(_ref3, _excluded3$1);
  var Element = as || defaultElementItem;
  var childrenArray = React.Children.toArray(children);
  var subMenu = childrenArray.find(function (child) {
    return child && child.type && child.type.__IS_ENTUR_MENU__;
  });
  var label = subMenu ? childrenArray.filter(function (child) {
    return child !== subMenu;
  }) : children;
  if (disabled) {
    return React.createElement(DisabledSideNavigationItem, _extends({
      ref: ref
    }, rest), label);
  }
  if (!subMenu) {
    return React.createElement(BaseSideNavigationItem, _extends({
      as: Element,
      active: active,
      ref: ref
    }, rest), label);
  }
  var isExpanded = forceExpandSubMenus || isActiveRecursively({
    props: {
      children: children,
      active: active
    }
  });
  return React.createElement(BaseSideNavigationItem, _extends({
    active: active,
    subMenu: isExpanded && subMenu,
    "aria-expanded": isExpanded,
    as: Element,
    ref: ref
  }, rest), label);
});
var _excluded$4 = ["defaultOpen", "open", "onToggle", "className", "children", "title", "icon"];
var SideNavigationGroup = function SideNavigationGroup(_ref) {
  var _ref$defaultOpen = _ref.defaultOpen,
    defaultOpen = _ref$defaultOpen === void 0 ? false : _ref$defaultOpen,
    open = _ref.open,
    onToggle = _ref.onToggle,
    className = _ref.className,
    children = _ref.children,
    title = _ref.title,
    icon = _ref.icon,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$4);
  var _useControllableProp = useControllableProp({
      prop: open,
      updater: onToggle,
      defaultValue: defaultOpen
    }),
    isOpen = _useControllableProp[0],
    setOpen = _useControllableProp[1];
  var _useSideNavigationCon = useSideNavigationContext(),
    isCollapsed = _useSideNavigationCon.isCollapsed;
  var _useShowDelayedLabel = useShowDelayedLabel(isCollapsed),
    showLabel = _useShowDelayedLabel[0];
  return React.createElement("div", _extends({
    className: classNames('eds-side-navigation-group', className)
  }, rest), React.createElement("button", {
    onClick: function onClick() {
      return setOpen(!isOpen);
    },
    type: "button",
    className: "eds-side-navigation-group__trigger",
    "aria-label": title + ", utvidbar meny, " + (isOpen ? 'åpen' : 'lukket')
  }, React.createElement("span", null, icon && React.createElement("span", {
    className: "eds-side-navigation-group__trigger-icon"
  }, icon), showLabel && title), showLabel && React.createElement(ExpandArrow, {
    open: isOpen,
    className: "eds-side-navigation-group__expand-icon"
  })), React.createElement(BaseExpand, {
    className: "eds-side-navigation-group__expand-content",
    open: isOpen
  }, children));
};
var _excluded$3 = ["active", "className", "as"];
var defaultElement = 'a';
var TopNavigationItem = function TopNavigationItem(_ref) {
  var _ref$active = _ref.active,
    active = _ref$active === void 0 ? false : _ref$active,
    className = _ref.className,
    as = _ref.as,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$3);
  var Element = as || defaultElement;
  return React.createElement(Element, _extends({
    className: classNames(['eds-top-navigation-item', className, {
      'eds-top-navigation-item--active': active
    }])
  }, rest));
};
var PaginationPage = function PaginationPage(_ref) {
  var children = _ref.children,
    className = _ref.className,
    selected = _ref.selected,
    disabled = _ref.disabled,
    onClick = _ref.onClick,
    ariaLabel = _ref['aria-label'],
    ariaDescribedby = _ref['aria-describedby'];
  return React.createElement("button", {
    className: classNames('eds-pagination__page', {
      'eds-pagination__page--selected': selected
    }, {
      'eds-pagination__page--disabled': disabled
    }, className),
    disabled: selected || disabled,
    type: "button",
    onClick: onClick,
    "aria-label": ariaLabel,
    "aria-describedby": ariaDescribedby,
    "aria-current": selected ? 'page' : false
  }, children);
};
var PaginationInput = function PaginationInput(_ref) {
  var currentPage = _ref.currentPage,
    pageCount = _ref.pageCount,
    _ref$label = _ref.label,
    label = _ref$label === void 0 ? 'Gå til side' : _ref$label,
    onPageChange = _ref.onPageChange;
  var _React$useState = React.useState(String(currentPage)),
    input = _React$useState[0],
    setInput = _React$useState[1];
  // If the currentPage prop changes, we want to reset the input field
  React.useEffect(function () {
    setInput(String(currentPage));
  }, [currentPage]);
  var handleSubmit = function handleSubmit(e) {
    e.preventDefault();
    e.stopPropagation();
    var pageNumber = Number(input);
    if (pageNumber === currentPage) {
      return;
    }
    if (Number.isNaN(pageNumber)) {
      pageNumber = currentPage;
      setInput(String(currentPage));
      return;
    }
    if (pageNumber > pageCount) {
      pageNumber = pageCount;
      setInput(String(pageCount));
    } else if (pageNumber < 1) {
      pageNumber = 1;
      setInput(String(1));
    }
    onPageChange(pageNumber);
  };
  var handleChange = function handleChange(e) {
    setInput(e.target.value);
  };
  return React.createElement("form", {
    onSubmit: handleSubmit,
    noValidate: true,
    "aria-label": "form"
  }, React.createElement("label", {
    className: "eds-pagination__input-wrapper"
  }, React.createElement("span", {
    className: "eds-pagination__input-label"
  }, label), React.createElement("input", {
    type: "number",
    max: pageCount,
    className: "eds-pagination__input-field",
    value: input,
    onChange: handleChange
  })));
};
var _excluded$2 = ["children", "className", "button", "position"],
  _excluded2 = ["children", "className", "onSelect", "disabled", "as"],
  _excluded3 = ["children", "as", "className", "onSelect", "disabled"];
var OverflowMenu = function OverflowMenu(_ref) {
  var children = _ref.children,
    className = _ref.className,
    button = _ref.button,
    _ref$position = _ref.position,
    position = _ref$position === void 0 ? 'right' : _ref$position,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$2);
  return React.createElement("div", null, React.createElement(Menu, null, !button ? React.createElement(IconButton, _extends({
    as: MenuButton,
    className: classNames(className, 'eds-overflow-menu__menu-button')
  }, rest), React.createElement(VerticalDotsIcon, null)) : cloneElement(button, _extends({
    as: MenuButton,
    className: classNames(className, 'eds-overflow-menu__menu-button')
  }, rest)), useContrast() ? React.createElement(Contrast, {
    className: classNames('eds-overflow-menu__menu-list'),
    as: MenuList,
    portal: false
  }, [children]) : React.createElement(MenuList, {
    className: classNames('eds-overflow-menu__menu-list', {
      'eds-overflow-menu__menu-list--left': position === 'left'
    }),
    portal: false
  }, [children])));
};
var OverflowMenuItem = /*#__PURE__*/React.forwardRef(function (_ref2, ref) {
  var children = _ref2.children,
    className = _ref2.className,
    onSelect = _ref2.onSelect,
    disabled = _ref2.disabled,
    _ref2$as = _ref2.as,
    as = _ref2$as === void 0 ? 'div' : _ref2$as,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  return React.createElement(MenuItem, _extends({
    className: classNames('eds-overflow-menu__item', {
      'eds-overflow-menu__item--disabled': disabled
    }, className),
    onSelect: onSelect,
    disabled: disabled,
    ref: ref,
    as: as
  }, rest), children);
});
var OverflowMenuLink = /*#__PURE__*/React.forwardRef(function (_ref3, ref) {
  var children = _ref3.children,
    _ref3$as = _ref3.as,
    as = _ref3$as === void 0 ? 'a' : _ref3$as,
    className = _ref3.className,
    onSelect = _ref3.onSelect,
    disabled = _ref3.disabled,
    rest = _objectWithoutPropertiesLoose(_ref3, _excluded3);
  return React.createElement(MenuLink, _extends({
    as: as,
    className: classNames('eds-overflow-menu__item', {
      'eds-overflow-menu__item--disabled': disabled
    }, className),
    onSelect: onSelect,
    disabled: disabled,
    ref: ref
  }, rest), children);
});
var _excluded$1 = ["className", "currentPage", "inputLabel", "onPageChange", "pageCount", "pageLabel", "previousPageLabel", "currentPageLabelForScreenreader", "lastPageLabelForScreenreader", "showInput", "numberOfResults", "resultsPerPage", "resultsPerPageOptions", "onResultsPerPageChange", "showNumberOfResultsLabel", "nextPageLabel", "showingResultsLabel", "hideNextButton", "hidePrevButton"];
var Pagination = function Pagination(_ref) {
  var className = _ref.className,
    currentPage = _ref.currentPage,
    inputLabel = _ref.inputLabel,
    onPageChange = _ref.onPageChange,
    pageCount = _ref.pageCount,
    _ref$pageLabel = _ref.pageLabel,
    pageLabel = _ref$pageLabel === void 0 ? function (pageNumber) {
      return "G\xE5 til side " + pageNumber;
    } : _ref$pageLabel,
    _ref$previousPageLabe = _ref.previousPageLabel,
    previousPageLabel = _ref$previousPageLabe === void 0 ? 'Gå til forrige side' : _ref$previousPageLabe,
    _ref$currentPageLabel = _ref.currentPageLabelForScreenreader,
    currentPageLabelForScreenreader = _ref$currentPageLabel === void 0 ? 'Nåværende side:' : _ref$currentPageLabel,
    _ref$lastPageLabelFor = _ref.lastPageLabelForScreenreader,
    lastPageLabelForScreenreader = _ref$lastPageLabelFor === void 0 ? ', siste side' : _ref$lastPageLabelFor,
    showInput = _ref.showInput,
    numberOfResults = _ref.numberOfResults,
    resultsPerPage = _ref.resultsPerPage,
    _ref$resultsPerPageOp = _ref.resultsPerPageOptions,
    resultsPerPageOptions = _ref$resultsPerPageOp === void 0 ? [10, 25, 50] : _ref$resultsPerPageOp,
    onResultsPerPageChange = _ref.onResultsPerPageChange,
    _ref$showNumberOfResu = _ref.showNumberOfResultsLabel,
    showNumberOfResultsLabel = _ref$showNumberOfResu === void 0 ? 'Vis' : _ref$showNumberOfResu,
    _ref$nextPageLabel = _ref.nextPageLabel,
    nextPageLabel = _ref$nextPageLabel === void 0 ? 'Gå til neste side' : _ref$nextPageLabel,
    _ref$showingResultsLa = _ref.showingResultsLabel,
    showingResultsLabel = _ref$showingResultsLa === void 0 ? function (minPage, maxPage, pageCount) {
      return "Viser resultat " + minPage + "\u2013" + maxPage + " av " + pageCount;
    } : _ref$showingResultsLa,
    _ref$hideNextButton = _ref.hideNextButton,
    hideNextButton = _ref$hideNextButton === void 0 ? false : _ref$hideNextButton,
    _ref$hidePrevButton = _ref.hidePrevButton,
    hidePrevButton = _ref$hidePrevButton === void 0 ? false : _ref$hidePrevButton,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$1);
  var isContrast = useContrast();
  var _useState = useState([]),
    listedEntries = _useState[0],
    setListedEntries = _useState[1];
  var paginationId = useRandomId('eds-pagination');
  var isFirstPostSelected = currentPage === 1;
  var isLastPostSelected = currentPage === pageCount;
  var noEllipsis = pageCount <= 7;
  var onlyLeadingEllipsis = !noEllipsis && currentPage < 5;
  var onlyTrailingEllipsis = !noEllipsis && pageCount - currentPage <= 3;
  useEffect(function () {
    if (pageCount < 1) return;
    if (noEllipsis) {
      setListedEntries(Array(pageCount).fill(null).map(function (_, i) {
        return i + 1;
      }));
    } else if (onlyLeadingEllipsis) {
      setListedEntries([1, 2, 3, 4, 5, '…', pageCount]);
    } else if (onlyTrailingEllipsis) {
      setListedEntries([1, '…', pageCount - 4, pageCount - 3, pageCount - 2, pageCount - 1, pageCount]);
    } else {
      // leading and trailing ellipsis
      setListedEntries([1, '…', currentPage - 1, currentPage, currentPage + 1, '…', pageCount]);
    }
  }, [noEllipsis, onlyLeadingEllipsis, onlyTrailingEllipsis, currentPage, pageCount]);
  if (pageCount < 1) {
    return null;
  }
  return React.createElement("nav", _extends({
    className: classNames('eds-pagination', className),
    "aria-label": "Paginering"
  }, rest), resultsPerPage && numberOfResults && React.createElement("div", {
    className: "eds-pagination__results"
  }, onResultsPerPageChange && React.createElement(Menu, null, function (_ref2) {
    var isOpen = _ref2.isOpen;
    return React.createElement(React.Fragment, null, React.createElement(Label, {
      as: "p",
      "aria-hidden": "true"
    }, showNumberOfResultsLabel), React.createElement(MenuButton, {
      className: classNames('eds-pagination-menu__menu-button', {
        'eds-pagination-menu__menu-button--open': isOpen
      })
    }, React.createElement(VisuallyHidden, null, showNumberOfResultsLabel, " "), resultsPerPage, isOpen ? React.createElement(UpArrowIcon, null) : React.createElement(DownArrowIcon, null)), React.createElement(MenuList, {
      className: classNames('eds-pagination-menu__menu-list', 'eds-overflow-menu__menu-list', {
        'eds-contrast': isContrast
      })
    }, resultsPerPageOptions.map(function (option, key) {
      return React.createElement(OverflowMenuItem, {
        key: key,
        onSelect: function onSelect() {
          return onResultsPerPageChange(option);
        }
      }, option);
    })));
  }), React.createElement(Label, {
    as: "p",
    className: "eds-pagination__results-label"
  }, showingResultsLabel((currentPage - 1) * resultsPerPage + 1, currentPage * resultsPerPage > numberOfResults ? numberOfResults : currentPage * resultsPerPage, numberOfResults))), React.createElement("div", {
    className: "eds-pagination__controls"
  }, !hidePrevButton && React.createElement(PaginationPage, {
    onClick: function onClick() {
      return onPageChange(currentPage - 1);
    },
    "aria-label": previousPageLabel,
    "aria-describedby": paginationId,
    disabled: isFirstPostSelected
  }, React.createElement(LeftArrowIcon, null)), listedEntries.map(function (entry, index) {
    return entry === '…' ? React.createElement(Ellipsis, {
      key: "ellipsis-" + index
    }) : React.createElement(PaginationPage, {
      selected: entry === currentPage,
      onClick: function onClick() {
        return onPageChange(entry);
      },
      "aria-label": "" + pageLabel(entry) + (entry === pageCount ? lastPageLabelForScreenreader : ''),
      "aria-describedby": entry !== currentPage ? paginationId : undefined,
      key: entry
    }, entry);
  }), !hideNextButton && React.createElement(PaginationPage, {
    onClick: function onClick() {
      return onPageChange(currentPage + 1);
    },
    "aria-label": nextPageLabel,
    "aria-describedby": paginationId,
    disabled: isLastPostSelected
  }, React.createElement(RightArrowIcon, null)), showInput && React.createElement(PaginationInput, {
    pageCount: pageCount,
    currentPage: currentPage,
    onPageChange: onPageChange,
    label: inputLabel
  })), React.createElement(VisuallyHidden, {
    id: paginationId
  }, currentPageLabelForScreenreader, " ", currentPage));
};
var Ellipsis = function Ellipsis() {
  return React.createElement("span", {
    className: "eds-pagination__ellipsis",
    "aria-hidden": "true"
  }, "\u2026");
};
var _excluded = ["activeIndex", "className", "interactive", "onStepClick", "showStepperIndex", "steps", "ariaLabelStep", "ariaLabelOf", "ariaLabelCompleted", "ariaLabelSummary"];
var Stepper = function Stepper(_ref) {
  var activeIndex = _ref.activeIndex,
    className = _ref.className,
    _ref$interactive = _ref.interactive,
    interactive = _ref$interactive === void 0 ? false : _ref$interactive,
    onStepClick = _ref.onStepClick,
    _ref$showStepperIndex = _ref.showStepperIndex,
    showStepperIndex = _ref$showStepperIndex === void 0 ? true : _ref$showStepperIndex,
    steps = _ref.steps,
    _ref$ariaLabelStep = _ref.ariaLabelStep,
    ariaLabelStep = _ref$ariaLabelStep === void 0 ? 'Steg' : _ref$ariaLabelStep,
    _ref$ariaLabelOf = _ref.ariaLabelOf,
    ariaLabelOf = _ref$ariaLabelOf === void 0 ? 'av' : _ref$ariaLabelOf,
    _ref$ariaLabelComplet = _ref.ariaLabelCompleted,
    ariaLabelCompleted = _ref$ariaLabelComplet === void 0 ? 'fullført' : _ref$ariaLabelComplet,
    _ref$ariaLabelSummary = _ref.ariaLabelSummary,
    ariaLabelSummary = _ref$ariaLabelSummary === void 0 ? "Stegindikator med " + steps.length + " steg, du er p\xE5 steg " + (activeIndex + 1) + " " + steps[activeIndex] + "," : _ref$ariaLabelSummary,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  return React.createElement("ol", _extends({
    className: classNames('eds-stepper', className),
    "aria-label": ariaLabelSummary
  }, rest), steps.map(function (step, i) {
    var isCurrent = i === activeIndex;
    var isInteractive = interactive && activeIndex > i;
    var Element = isInteractive ? 'button' : 'div';
    var isCompleted = activeIndex > i;
    var currentStepSummary = ariaLabelStep + " " + (i + 1) + " " + ariaLabelOf + " " + steps.length + ", " + step + " " + (isCompleted ? ", " + ariaLabelCompleted : '');
    var props = isInteractive ? {
      onClick: function onClick() {
        return onStepClick == null ? void 0 : onStepClick(i);
      }
    } : {};
    return React.createElement("li", {
      key: step,
      className: "eds-stepper__step__wrapper"
    }, React.createElement(Element, _extends({
      className: classNames('eds-stepper__step', {
        'eds-stepper__step--active': isCurrent
      }, {
        'eds-stepper__step--completed': isCompleted
      }, {
        'eds-stepper__step--interactive': isInteractive
      }),
      "aria-current": isCurrent ? 'step' : undefined,
      type: Element === 'button' ? 'button' : undefined
    }, props), React.createElement("div", {
      className: "eds-stepper__step__line",
      "aria-hidden": true
    }), React.createElement("span", {
      className: "eds-stepper__step__label",
      "aria-hidden": true
    }, showStepperIndex && i + 1 + '.', " ", step), React.createElement(VisuallyHidden, null, currentStepSummary)));
  }));
};
warnAboutMissingStyles('menu', 'expand', 'icons', 'typography', 'button', 'layout', 'a11y');
export { BreadcrumbItem, BreadcrumbNavigation, CollapsibleSideNavigation, OverflowMenu, OverflowMenuItem, OverflowMenuLink, Pagination, SideNavigation, SideNavigationGroup, SideNavigationItem, Stepper, TopNavigationItem, useSideNavigationContext };